import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from '../../../api/axios';
import './AdminNavigation.css';

interface NavigationStats {
  users: number;
  candidates: number;
  respondents: number;
}

interface AdminNavigationProps {
  users?: number;
  candidates?: number;
  respondents?: number;
}

interface CachedStats {
  data: NavigationStats;
  timestamp: number;
}

const CACHE_DURATION = 60 * 1000; // 1分間キャッシュ

const AdminNavigation: React.FC<AdminNavigationProps> = ({ users=undefined, candidates=undefined, respondents=undefined }) => {
  const [stats, setStats] = useState<NavigationStats>({
    users: 0,
    candidates: 0,
    respondents: 0
  });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchStats();
  }, [users, candidates, respondents]);

  const fetchStats = async () => {
    try {
      const cachedData = localStorage.getItem('admin_navigation_stats');
      if (cachedData) {
        const cached: CachedStats = JSON.parse(cachedData);
        const now = Date.now();

        if (now - cached.timestamp < CACHE_DURATION) {
          if (users !== undefined) {
            cached.data.users = users;
          }
          if (candidates !== undefined) {
            cached.data.candidates = candidates;
          }
          if (respondents !== undefined) {
            cached.data.respondents = respondents;
          }
          setStats(cached.data);

          localStorage.setItem('admin_navigation_stats', JSON.stringify(cached));
          return;
        }
      }

      const response = await axios.get('/admin/stats');
      const statsData = response.data;

      setStats(statsData);

      const cacheData: CachedStats = {
        data: statsData,
        timestamp: Date.now()
      };
      localStorage.setItem('admin_navigation_stats', JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error fetching stats:', error);

      const cachedData = localStorage.getItem('admin_navigation_stats');
      if (cachedData) {
        const cached: CachedStats = JSON.parse(cachedData);
        setStats(cached.data);
      }
    }
  };

  const getActiveTab = () => {
    if (location.pathname.includes('/admin/clients')) return 'clients';
    if (location.pathname.includes('/admin/candidates')) return 'candidates';
    if (location.pathname.includes('/admin/respondents')) return 'respondents';
    if (location.pathname.includes('/admin/surveys')) return 'surveys';
    if (location.pathname.includes('/admin/templates')) return 'templates';
    return '';
  };

  const activeTab = getActiveTab();

  return (
    <div className={`admin-navigation-container`}>
      <div className="menu-navi-container">
        <div 
          className={`menu-navi-label ${activeTab === 'clients' ? 'active' : ''}`}
          onClick={() => navigate('/admin/clients')}
        >
          クライアント一覧 <span className="record-count">({stats.users})</span>
        </div>
        <div 
          className={`menu-navi-label ${activeTab === 'candidates' ? 'active' : ''}`}
          onClick={() => navigate('/admin/candidates')}
        >
          候補者一覧 <span className="record-count">({stats.candidates})</span>
        </div>
        <div 
          className={`menu-navi-label ${activeTab === 'respondents' ? 'active' : ''}`}
          onClick={() => navigate('/admin/respondents')}
        >
          回答者一覧 <span className="record-count">({stats.respondents})</span>
        </div>
        <div 
          className={`menu-navi-label ${activeTab === 'surveys' ? 'active' : ''}`}
          onClick={() => navigate('/admin/surveys')}
        >
          アンケート設定
        </div>
      </div>
    </div>
  );
};

export default AdminNavigation;
